import { useLayoutEffect, useMemo } from 'react';

import { BRANDING_VAR_KEYS, buildBrandingVars } from '../../helpers/brandColors';
import { PATIENT_BRANDING_CACHE_KEY as CACHE_KEY } from '../../constants/sessionStorageKeys';

import { PracticeBrandingContext } from './PracticeBrandingContext';

const readCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCache = (practiceId, branding) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ practiceId, branding }));
  } catch {
    // sessionStorage unavailable — branding just re-fetches next load.
  }
};

const clearCache = () => {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    // Nothing cached means nothing to leak.
  }
};

const applyVars = (vars) => {
  const root = document.documentElement;
  Object.entries(vars).forEach(([key, value]) => root.style.setProperty(key, value));
};

const removeVars = () => {
  const root = document.documentElement;
  BRANDING_VAR_KEYS.forEach((key) => root.style.removeProperty(key));
};

export const PracticeBrandingContextProvider = ({ branding, practiceId = null, children }) => {
  const primaryColor = branding?.primary_color || null;
  const secondaryColor = branding?.secondary_color || null;

  const isResolved = practiceId != null || branding !== undefined;

  // On mount, synchronously re-apply the last-known branding from sessionStorage so a reload
  // paints the practice theme immediately instead of flashing the default until the API returns.
  useLayoutEffect(() => {
    const cached = readCache();
    if (cached?.branding) applyVars(buildBrandingVars(cached.branding));

    return removeVars;
  }, []);

  useLayoutEffect(() => {
    if (!isResolved) return;

    const effective = { primary_color: primaryColor, secondary_color: secondaryColor };
    const vars = buildBrandingVars(effective);

    removeVars();

    if (Object.keys(vars).length) {
      applyVars(vars);
      if (practiceId != null) writeCache(practiceId, effective);
    } else if (practiceId != null) {
      clearCache();
    }
  }, [isResolved, practiceId, primaryColor, secondaryColor]);

  const context = useMemo(() => ({ primaryColor, secondaryColor }), [primaryColor, secondaryColor]);

  return (
    <PracticeBrandingContext.Provider value={context}>{children}</PracticeBrandingContext.Provider>
  );
};
